/**
 * ambient_models — the ambient life wave's creature bodies (2026-09-29): load a cooked `prop:herd_*` / `prop:critter_*`
 * model, lay it out in ONE frame, and animate it in the vertex shader — Meshy's auto-rig is for people, so a grazer's
 * legs and neck, a butterfly's wings and a crab's legs are bent procedurally, the way the birds' wings are.
 *
 * THE FRAME (every kind, after `normalizeCreature`): the head faces +Z (render-glb-views + a slice measure: all six
 * models were generated head +Z; the butterfly UPRIGHT, laid flat here), the geometry is scaled so its sizing length is
 * 1 (a grazer's nose-to-tail, a flyer's wingspan, a crab's width across the legs), and a walker's feet stand at y = 0.
 * An instance is `T(pos − anchor) · R_y(yaw) · S(scale)`: its forward is (sin yaw, cos yaw).
 *
 * THE SHADER: `positionGeometry` (the raw, normalised model position — inside a positionNode `positionLocal` is already
 * instance-transformed, the MEASURE/DISPLACE law) decides which part a vertex belongs to; the part's bend is a DELTA in
 * model space, rotated by the instance's yaw and scaled into the instance's space, then added to `positionLocal`. The same
 * delta with the previous frame's phase is assigned to `positionPrevious` (the positionPrevious LAW — motion vectors).
 *
 * TWO per-instance vec4s ride beside the instance matrix: `aAnim` (the kind's phases) and `aPose` (yaw, scale, a packed
 * tint, 0) — six vertex buffers with position / normal / uv, seven with the velocity pass's previous matrix, under
 * WebGPU's eight. Normals are not bent (a swinging leg lights like a standing one — invisible at a grazer's range).
 */
import * as THREE from 'three/webgpu';
import { Fn, attribute, positionLocal, positionGeometry, positionPrevious, sin, cos, abs, max, mix, float, vec3, vec4, step, smoothstep, sign, floor, mod, texture, uv, clamp } from 'three/tsl';

export type CreatureKind = 'quadruped' | 'wings' | 'crab';

export interface QuadrupedRig {
  /** The belly's underside (normalised y): below it a vertex is leg. */
  legTop: number;
  blend: number;
  frontHipZ: number;
  backHipZ: number;
  /** How far along the body (z) from its hip a leg reaches: a vertex farther off is body, not leg — the woolcrag's
   *  hair skirt hangs below the belly between the legs and swung like a paddle before this. */
  legHalf: number;
  /** How far out from the midline (|x|) a leg stands: a skirt hanging wider than the hooves stays put too. */
  legX: number;
  /** The neck's pivot (normalised y, z) and the muzzle's z. */
  neckY: number;
  neckZ: number;
  muzzleZ: number;
  /** How far the muzzle still stands above the grass at the graze angle — the neck's extra drop covers it. */
  neckDrop: number;
  grazeNeck: number;
  height: number;
}

export interface CreatureParts {
  kind: CreatureKind;
  geometry: THREE.BufferGeometry;
  material: THREE.MeshStandardNodeMaterial;
  rig: QuadrupedRig | null;
  /** Normalised height (the geometry's y extent). */
  height: number;
}

/** Pack an sRGB hex into one float (24 bits are exact in f32); the shader unpacks and linearises it. */
export function packTint(hex: number): number { return hex & 0xffffff; }

/** The first mesh of a loaded representation, its geometry baked into the representation's frame. */
function firstMesh(object: THREE.Object3D): THREE.Mesh | null {
  object.updateMatrixWorld(true);
  let m: THREE.Mesh | null = null;
  object.traverse((o) => { if (!m && (o as THREE.Mesh).isMesh) m = o as THREE.Mesh; });
  return m;
}

/**
 * A float copy of a quantized attribute. The cooker stores positions as NORMALIZED int16 under a node scale (meshopt
 * quantization, measured on the served files), and moving such an attribute past ±1 WRAPS: three's `normalize()` does
 * not clamp and the Int16Array wraps modulo 2¹⁶. Feet lifted to y = 0 put every creature taller than a metre past +1 —
 * the copperhorn (1.68 m) and the woolcrag (1.23 m) drew as tall streaked columns while the 0.89 m fenback was fine.
 */
function floatCopy(a: THREE.BufferAttribute | THREE.InterleavedBufferAttribute): THREE.BufferAttribute {
  if (!(a as THREE.InterleavedBufferAttribute).isInterleavedBufferAttribute && a.array instanceof Float32Array && !a.normalized) return a as THREE.BufferAttribute;
  const n = a.count, k = a.itemSize, out = new Float32Array(n * k);
  for (let i = 0; i < n; i++) {
    out[i * k] = a.getX(i);
    if (k > 1) out[i * k + 1] = a.getY(i);
    if (k > 2) out[i * k + 2] = a.getZ(i);
    if (k > 3) out[i * k + 3] = a.getW(i);
  }
  return new THREE.BufferAttribute(out, k);
}

/**
 * Normalise a creature's geometry into THE FRAME. `layFlat` turns an upright flyer (head +Y, wings in XY) onto its
 * belly (head +Z, wings in XZ). Returns the geometry and its normalised height.
 */
export function normalizeCreature(src: THREE.BufferGeometry, worldMatrix: THREE.Matrix4, kind: CreatureKind, layFlat: boolean,
  opts?: { unit?: 'xz'; ground?: boolean }): { geo: THREE.BufferGeometry; height: number } {
  const geo = src.clone();
  // THE WRAP LAW: dequantize before any transform (floatCopy)
  for (const name of ['position', 'normal']) { const a = geo.getAttribute(name); if (a) geo.setAttribute(name, floatCopy(a)); }
  geo.applyMatrix4(worldMatrix);
  if (layFlat) geo.rotateX(Math.PI / 2); // head +Y → +Z (rotateX: z' = y), wings into the XZ plane
  geo.computeBoundingBox();
  const bb = geo.boundingBox!;
  const size = new THREE.Vector3(); bb.getSize(size);
  const c = new THREE.Vector3(); bb.getCenter(c);
  // `ground` (the wild creatures, 2026-10-05): every body stands on y = 0 — a hovering drifter lifts itself in the shader
  geo.translate(-c.x, kind === 'wings' && !opts?.ground ? -c.y : -bb.min.y, -c.z);
  // `unit: 'xz'` (the wild creatures): the sizing length is the longer of x and z — WildsModule's SPECIES_LEN convention
  const unit = opts?.unit === 'xz' ? Math.max(size.x, size.z) : kind === 'quadruped' ? size.z : size.x;
  const s = 1 / Math.max(1e-6, unit);
  geo.scale(s, s, s);
  // every attribute the shader never reads is a vertex buffer on WebGPU (the birds' strip)
  for (const name of ['tangent', 'uv1', 'uv2', 'uv3', 'color', 'skinIndex', 'skinWeight']) if (geo.getAttribute(name)) geo.deleteAttribute(name);
  geo.computeBoundingBox();
  geo.computeBoundingSphere();
  return { geo, height: size.y * s };
}

/** Where a normalised quadruped's legs and neck are, measured from its vertices. */
export function quadrupedRig(geo: THREE.BufferGeometry, grazeNeck: number, opts?: { footZMax?: number }): QuadrupedRig {
  const p = geo.getAttribute('position');
  // `footZMax` (the wild creatures, 2026-10-05): the ridge stalker's tail tip rests on the ground behind its hind feet —
  // a low vertex farther than this from the middle (|z|) is a nose or a tail, never a foot. Herds pass nothing (no limit).
  const footZMax = opts?.footZMax ?? Infinity;
  let H = 0, belly = Infinity;
  for (let i = 0; i < p.count; i++) { const y = p.getY(i); if (y > H) H = y; if (Math.abs(p.getZ(i)) < 0.1 && y < belly) belly = y; }
  if (!Number.isFinite(belly) || belly <= 0.02 || belly > H * 0.8) belly = H * 0.45;
  // THE HOOVES LOCATE THE LEGS: only feet touch the ground (y = 0 is the lowest vertex) — a hair skirt or a lowered head
  // hangs above them, and the old mean over everything under 0.6 × the belly pulled the woolcrag's hips into its skirt
  const footTop = Math.min(0.05, belly * 0.35);
  const fzs: number[] = [], bzs: number[] = [], xs: number[] = [];
  for (let i = 0; i < p.count; i++) {
    if (p.getY(i) > footTop) continue;
    const z = p.getZ(i);
    if (Math.abs(z) > footZMax) continue;
    (z > 0 ? fzs : bzs).push(z);
    xs.push(Math.abs(p.getX(i)));
  }
  xs.sort((a, b) => a - b);
  const q = (xs: number[], t: number): number => xs[Math.min(xs.length - 1, Math.max(0, Math.round(t * (xs.length - 1))))];
  fzs.sort((a, b) => a - b); bzs.sort((a, b) => a - b);
  const frontHipZ = fzs.length ? q(fzs, 0.5) : 0.3, backHipZ = bzs.length ? q(bzs, 0.5) : -0.3;
  // a leg reaches its feet's spread plus the slope a leg makes between the hip and the hoof, never past the other pair
  const spread = Math.max(fzs.length ? (q(fzs, 0.9) - q(fzs, 0.1)) / 2 : 0, bzs.length ? (q(bzs, 0.9) - q(bzs, 0.1)) / 2 : 0);
  const legHalf = Math.min(Math.max(0.1, spread + 0.08), Math.max(0.1, (frontHipZ - backHipZ) * 0.45));
  const legX = xs.length ? Math.max(0.08, xs[Math.min(xs.length - 1, Math.round(0.95 * (xs.length - 1)))] + 0.06) : 0.5;
  const neckZ = frontHipZ + 0.05;
  const neckY = belly + (H - belly) * 0.55;
  let muzzleZ = neckZ + 0.1, muzzleY = neckY;
  for (let i = 0; i < p.count; i++) { const y = p.getY(i), z = p.getZ(i); if (y > belly + 0.05 && z > muzzleZ) { muzzleZ = z; muzzleY = y; } }
  const ny = muzzleY - neckY, nz = muzzleZ - neckZ;
  const grazedY = neckY + ny * Math.cos(grazeNeck) - nz * Math.sin(grazeNeck);
  return { legTop: belly, blend: 0.06, frontHipZ, backHipZ, legHalf, legX, neckY, neckZ, muzzleZ, neckDrop: Math.max(0, grazedY - 0.06), grazeNeck, height: H };
}

type N = ReturnType<typeof float>;

/** The model-space delta of a vertex for this kind, at a phase. Exported for the wild creatures' quadrupeds
 *  (lib/wild_models.ts, 2026-10-05) — the same lateral-sequence walk, read from per-object uniforms there. */
export function deltaFor(kind: CreatureKind, rig: QuadrupedRig | null, g: ReturnType<typeof vec3>, anim: ReturnType<typeof vec4>, phase: N): ReturnType<typeof vec3> {
  if (kind === 'quadruped' && rig) {
    const front = step(0, g.z), left = step(0, g.x);
    // a lateral-sequence walk: left hind 0, left fore ¼, right hind ½, right fore ¾ of a cycle
    const off = float(1).sub(left).mul(0.5).add(front.mul(0.25)).mul(Math.PI * 2);
    const hipZ = mix(float(rig.backHipZ), float(rig.frontHipZ), front);
    // leg = under the belly AND within the leg's reach of its hip along the body (a skirt between the legs stays put)
    const legW = float(1).sub(smoothstep(rig.legTop - rig.blend, rig.legTop, g.y))
      .mul(float(1).sub(smoothstep(rig.legHalf, rig.legHalf + 0.06, abs(g.z.sub(hipZ)))))
      .mul(float(1).sub(smoothstep(rig.legX, rig.legX + 0.05, abs(g.x))));
    const a = sin(phase.add(off)).mul(anim.w).mul(legW);
    const dy = g.y.sub(rig.legTop), dz = g.z.sub(hipZ);
    const ca = cos(a), sa = sin(a);
    const legDy = dy.mul(ca).sub(dz.mul(sa)).add(rig.legTop).sub(g.y);
    const legDz = dy.mul(sa).add(dz.mul(ca)).add(hipZ).sub(g.z);
    // the neck swings the head; the height gate keeps the chest over the fore legs still — but a jaw ahead of the fore
    // legs is HEAD at any height (gated by height, the fenback's and woolcrag's chins sheared into a flat shovel)
    const headZ = rig.frontHipZ + rig.legHalf;
    const neckW = smoothstep(rig.neckZ - 0.05, rig.neckZ + 0.08, g.z).mul(max(smoothstep(rig.legTop + 0.02, rig.legTop + 0.12, g.y), smoothstep(headZ, headZ + 0.06, g.z)));
    const b = anim.z.mul(neckW);
    const ny = g.y.sub(rig.neckY), nz = g.z.sub(rig.neckZ);
    const cb = cos(b), sb = sin(b);
    const reach = clamp(g.z.sub(rig.neckZ).div(Math.max(0.05, rig.muzzleZ - rig.neckZ)), 0, 1);
    const drop = max(anim.z, 0).div(Math.max(0.1, rig.grazeNeck)).mul(rig.neckDrop).mul(reach).mul(neckW);
    const headDy = ny.mul(cb).sub(nz.mul(sb)).add(rig.neckY).sub(drop).sub(g.y);
    const headDz = ny.mul(sb).add(nz.mul(cb)).add(rig.neckZ).sub(g.z);
    const bob = abs(sin(phase)).mul(anim.w).mul(0.012);
    return vec3(0, legDy.add(headDy).add(bob), legDz.add(headDz));
  }
  if (kind === 'wings') {
    const hinge = 0.035;
    const r = max(abs(g.x).sub(hinge), 0);
    const ang = phase; // the wing angle itself (rad), written by the CPU
    return vec3(sign(g.x).mul(r).mul(cos(ang).sub(1)), r.mul(sin(ang)), 0);
  }
  // crab: the legs past the shell lift in a travelling ripple; the shell bobs
  const shell = 0.24;
  const legW = smoothstep(shell, shell + 0.08, abs(g.x));
  const side = step(0, g.x).mul(Math.PI);
  const lift = max(sin(phase.add(side).add(g.z.mul(14))), 0).mul(anim.z).mul(legW).mul(0.07);
  const bob = abs(sin(phase.mul(2))).mul(anim.z).mul(0.012);
  return vec3(0, lift.add(bob), 0);
}

/** A node material for a creature from its cooked material, with the kind's vertex animation (and a tint for the flyers). */
export function creatureMaterial(src: THREE.MeshStandardMaterial | null, kind: CreatureKind, rig: QuadrupedRig | null, tinted: boolean): THREE.MeshStandardNodeMaterial {
  const mat = new THREE.MeshStandardNodeMaterial();
  mat.name = `ambient-${kind}`;
  if (src) {
    mat.normalMap = src.normalMap ?? null;
    mat.roughnessMap = src.roughnessMap ?? null; mat.metalnessMap = src.metalnessMap ?? null;
    mat.roughness = src.roughness ?? 0.85; mat.metalness = src.metalness ?? 0;
  }
  mat.side = THREE.DoubleSide;
  const aAnim = attribute('aAnim', 'vec4') as unknown as ReturnType<typeof vec4>;
  const aPose = attribute('aPose', 'vec4') as unknown as ReturnType<typeof vec4>;
  const map = src?.map ?? null;
  if (tinted) {
    // aPose.z is a packed sRGB hex; the classic material.color path linearises, so this does too (≈ 2.2)
    const t = aPose.z;
    const rgb = vec3(floor(t.div(65536)), mod(floor(t.div(256)), 256), mod(t, 256)).div(255).pow(vec3(2.2, 2.2, 2.2));
    mat.colorNode = map ? texture(map, uv()).mul(vec4(rgb, 1)) : vec4(rgb, 1);
  } else if (map) {
    mat.map = map;
  } else if (src) {
    mat.color.copy(src.color);
  }
  const toInstance = (d: ReturnType<typeof vec3>): ReturnType<typeof vec3> => {
    const c = cos(aPose.x), s = sin(aPose.x);
    return vec3(d.x.mul(c).add(d.z.mul(s)), d.y, d.z.mul(c).sub(d.x.mul(s))).mul(aPose.y) as unknown as ReturnType<typeof vec3>;
  };
  // r185/WebGPU LAW: an assign() needs an enclosing Fn() stack (the birds' flyer vanished without one)
  mat.positionNode = Fn(() => {
    const g = positionGeometry as unknown as ReturnType<typeof vec3>;
    positionPrevious.assign(positionLocal.add(toInstance(deltaFor(kind, rig, g, aAnim, aAnim.y as unknown as N))));
    return positionLocal.add(toInstance(deltaFor(kind, rig, g, aAnim, aAnim.x as unknown as N)));
  })();
  return mat;
}

/** Build a creature's drawable parts from a loaded representation. Null when it holds no mesh. */
export function creatureParts(object: THREE.Object3D, kind: CreatureKind, opts: { layFlat?: boolean; grazeNeck?: number; tinted?: boolean }): CreatureParts | null {
  const mesh = firstMesh(object);
  if (!mesh) return null;
  const { geo, height } = normalizeCreature(mesh.geometry, mesh.matrixWorld, kind, !!opts.layFlat);
  const rig = kind === 'quadruped' ? quadrupedRig(geo, opts.grazeNeck ?? 0.8) : null;
  const src = (Array.isArray(mesh.material) ? mesh.material[0] : mesh.material) as THREE.MeshStandardMaterial;
  return { kind, geometry: geo, material: creatureMaterial(src ?? null, kind, rig, !!opts.tinted), rig, height };
}

/**
 * One instanced draw of one creature at one LOD: the geometry's two per-instance vec4s, a fixed capacity, instance
 * matrices RELATIVE TO THE PARENT (an anchor group near the viewer — float32 at 900 km is centimetres).
 */
export class CreatureBatch {
  readonly mesh: THREE.InstancedMesh;
  private readonly anim: THREE.InstancedBufferAttribute;
  private readonly pose: THREE.InstancedBufferAttribute;
  private n = 0;
  private readonly m = new THREE.Matrix4();
  private readonly q = new THREE.Quaternion();
  private readonly p = new THREE.Vector3();
  private readonly s = new THREE.Vector3();
  private readonly up = new THREE.Vector3(0, 1, 0);

  constructor(parent: THREE.Object3D, parts: CreatureParts, capacity: number, name: string, readonly release: () => void) {
    const geo = parts.geometry;
    this.anim = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4);
    this.pose = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4);
    this.anim.setUsage(THREE.DynamicDrawUsage); this.pose.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aAnim', this.anim);
    geo.setAttribute('aPose', this.pose);
    const mesh = new THREE.InstancedMesh(geo, parts.material, capacity);
    mesh.count = 0; mesh.name = name; mesh.castShadow = false; mesh.receiveShadow = true;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.visible = false;
    this.mesh = mesh;
    parent.add(mesh);
  }

  get capacity(): number { return this.mesh.instanceMatrix.count; }
  begin(): void { this.n = 0; }

  /** One creature: position RELATIVE to the parent, heading, uniform scale, the kind's four phases and a tint. */
  put(x: number, y: number, z: number, yaw: number, scale: number, a0: number, a1: number, a2: number, a3: number, tint = 0xffffff): boolean {
    if (this.n >= this.capacity) return false;
    const i = this.n++;
    this.q.setFromAxisAngle(this.up, yaw);
    this.p.set(x, y, z); this.s.set(scale, scale, scale);
    this.m.compose(this.p, this.q, this.s);
    this.mesh.setMatrixAt(i, this.m);
    this.anim.setXYZW(i, a0, a1, a2, a3);
    this.pose.setXYZW(i, yaw, scale, packTint(tint), 0);
    return true;
  }

  end(hidden = false): void {
    const n = this.n, mesh = this.mesh;
    mesh.count = n;
    mesh.visible = n > 0 && !hidden;
    if (n > 0) {
      // PERF (shader sweep C, 2026-10-06; THE UPLOAD-RANGE LAW): a bare needsUpdate uploads the whole CAPACITY of all three
      // buffers every frame; only the n live slots were written.
      const im = mesh.instanceMatrix;
      im.clearUpdateRanges(); im.addUpdateRange(0, n * 16); im.needsUpdate = true;
      this.anim.clearUpdateRanges(); this.anim.addUpdateRange(0, n * 4); this.anim.needsUpdate = true;
      this.pose.clearUpdateRanges(); this.pose.addUpdateRange(0, n * 4); this.pose.needsUpdate = true;
      mesh.computeBoundingSphere(); // the draw is frustum-culled by the herd's real extent
    }
  }

  get count(): number { return this.n; }

  dispose(): void {
    this.mesh.removeFromParent();
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
    this.mesh.dispose();
    this.release();
  }
}
